import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Card, Input } from "@atlas/ui/web";
import { atlas, storeRole } from "../lib/sdk";
import { setAccountKey, normalizarConta } from "../lib/query-account";
import { AtlasLogo } from "../components/AtlasBrand";

/** Estado quando o backend pede TOTP: nao ha access_token ainda, so o mfa_token
 *  que sera trocado no /v1/auth/verify-2fa. */
type Pending2FA = { mfa_token: string; hint?: string };

export function LoginPage() {
  const nav = useNavigate();
  const [identifier, setId] = useState("");
  const [password, setPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending2FA | null>(null);
  const [codigo, setCodigo] = useState("");

  async function submit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await atlas.login({ identifier, password });
      if ("requires_2fa" in res && res.requires_2fa) {
        setPending({ mfa_token: res.mfa_token, hint: res.hint });
        setLoading(false);
        return;
      }
      // Aqui res e AuthSuccess (o narrowing do TS ja garante).
      const ok = res as { role: "servidor" | "banco" | "averbadora" | "prefeitura"; senha_temporaria?: boolean };
      finalize(ok.role, ok.senha_temporaria);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha no login");
    } finally {
      setLoading(false);
    }
  }

  async function submit2FA(e: FormEvent) {
    e.preventDefault();
    if (!pending) return;
    setLoading(true);
    setError(null);
    try {
      const res = await atlas.verify2fa(pending.mfa_token, codigo.replace(/\D/g, ""));
      finalize(res.role, res.senha_temporaria);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Código inválido");
    } finally {
      setLoading(false);
    }
  }

  function finalize(
    role: "servidor" | "banco" | "averbadora" | "prefeitura",
    senhaTemporaria?: boolean,
  ) {
    storeRole(role);
    // Escopa o cache do React Query nesta conta (identifier = e-mail/CPF, unico
    // por conta). Contas diferentes -> hash diferente -> sem vazamento; voltar
    // pra esta conta reaproveita o cache dela (sem "Carregando" em todo menu).
    // normalizarConta: CPF com e sem mascara tem que dar a MESMA chave,
    // senao o estado por conta (cache + notificacoes lidas) se perde conforme
    // o jeito que a pessoa digitou. Cliente 13/08/2026.
    setAccountKey(normalizarConta(identifier, role));
    // Sub-usuario entrando com a senha que o admin definiu: troca primeiro.
    // Enquanto ele nao trocar, a senha e' conhecida por duas pessoas.
    //
    // A condicao era `role === "banco"` fixa. Cada perfil tem a SUA tela, entao
    // o mapa e' explicito: perfil sem tela de troca nao ganha desvio, em vez de
    // ser mandado para uma rota que nao existe.
    const telaDeTroca: Partial<Record<typeof role, string>> = {
      banco: "/banco/trocar-senha",
      prefeitura: "/prefeitura/trocar-senha",
    };
    if (senhaTemporaria && telaDeTroca[role]) {
      nav(telaDeTroca[role]!, { replace: true });
      return;
    }
    const target = role === "servidor" ? "/servidor/selecionar-matricula" : `/${role}/dashboard`;
    nav(target, { replace: true });
  }

  function cancelar2FA() {
    setPending(null);
    setCodigo("");
    setError(null);
  }

  return (
    <div className="auth-shell">
      <Card style={{ background: "var(--surface)" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, marginBottom: 24, textAlign: "center" }}>
          <AtlasLogo height={96} />
          <div style={{ fontSize: ".82rem", color: "var(--text-dim)" }}>
            {pending
              ? "Verificação em duas etapas"
              : "Entre com seu CPF (servidor) ou e-mail (consignatárias / órgão público)"}
          </div>
        </div>

        {pending ? (
          <form onSubmit={submit2FA} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <p style={{ margin: 0, fontSize: ".9rem", color: "var(--text-muted)" }}>
              {pending.hint ?? "Informe o código de 6 dígitos do seu aplicativo autenticador."}
            </p>
            <Input
              label="Código 2FA"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              required
              autoFocus
            />
            {error ? (
              <div style={{ fontSize: ".88rem", color: "var(--danger-500)" }}>{error}</div>
            ) : null}
            <Button type="submit" disabled={loading || codigo.length < 6}>
              {loading ? "Verificando..." : "Entrar →"}
            </Button>
            <button
              type="button"
              onClick={cancelar2FA}
              style={{ background: "transparent", border: 0, color: "var(--text-muted)", fontSize: ".88rem", cursor: "pointer" }}
            >
              ← Voltar
            </button>
          </form>
        ) : (
          <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Sem placeholder de exemplo — o label ja diz o que entra aqui, e o
                CPF ficticio dentro do campo parecia valor preenchido. Cliente 05/08/2026. */}
            <Input
              label="CPF ou e-mail"
              value={identifier}
              onChange={(e) => setId(e.target.value)}
              autoComplete="username"
              required
            />
            <Input
              label="Senha"
              type="password"
              value={password}
              onChange={(e) => setPwd(e.target.value)}
              autoComplete="current-password"
              required
            />
            {error ? (
              <div style={{ fontSize: ".88rem", color: "var(--danger-500)" }}>{error}</div>
            ) : null}
            <Button type="submit" disabled={loading}>
              {loading ? "Entrando..." : "Entrar"}
            </Button>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: ".88rem" }}>
              <Link to="/esqueci-senha" style={{ color: "var(--text-muted)" }}>Esqueci minha senha</Link>
              <Link to="/primeiro-acesso" style={{ color: "var(--accent)", fontWeight: 600 }}>Primeiro acesso</Link>
            </div>
          </form>
        )}

        {/* Bloco "Sandbox" com credenciais de teste REMOVIDO (cliente 03/08/2026):
            expunha usuario/senha de todos os perfis na tela de login publica. */}
      </Card>
    </div>
  );
}
